import _ from 'lodash';
import * as React from 'react';
import cn from 'classnames';
import PageTitle from '../../pages/components/pageTitle';
import {
  WaiverWorkItemDetails,
  WaiverWorkItem,
  WaiverWorkItemAction,
} from '../../types/waiverWorkItem';
import { AirlineInfoLink, Waiver } from '../../types/waiver';
import styles from './work-item-details.module.css';
import WaiverAssociationModal from './WaiverAssociationModal';
import WaiverEmail from './WaiverEmail';
import WaiverWebsiteChange from './WaiverWebsiteChange';
import AirlineSnoozer from './AirlineSnoozer';
import TextArea from '../../components/forms/TextArea';
import { ButtonPrimary } from '../../components/forms/Buttons';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getDateFormat } from '../../components/dateTimePicker/DateTimePickerCommon';

import { EventStatus } from '../../types/components';
import WaiverWorkItemActionPicker from './WaiverWorkItemActionPicker';

interface WaiverWorkItemComponentProps {
  workItemDetails: WaiverWorkItemDetails;
  workItem: WaiverWorkItem;
  workItemWaivers: Waiver[];
  workItemActions: WaiverWorkItemAction[];
  executeAction: (action: WaiverWorkItemAction, workItem: WaiverWorkItem) => void;
  waiverToChangeAssociation: Waiver | null;
  changeWaiverAssociation: (waiver: Waiver, number: number) => void;
  endChangeWaiverAssociation: () => void;
  feedbackValue: string;
  feedbackOnChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  feedbackOnSubmit: () => void;
  snoozeNotificationsForAirline: (airlineCode: string, hours: number) => void;
  updateStatus: EventStatus;
};

const WaiverWorkItemComponent: React.FC<WaiverWorkItemComponentProps> = (props) => {
  const workItem: WaiverWorkItem = props.workItem;

  const workItemContent: React.ReactElement =
    workItem.source === 'SCRAPER' ? (
      <WaiverWebsiteChange
        websiteUrl={props.workItemDetails.websiteUrl}
        originalImageData={props.workItemDetails.originalImageData}
        newImageData={props.workItemDetails.newImageData}
        diffImageData={props.workItemDetails.diffImageData}
        childResults={props.workItemDetails.childResults}
      />
    ) : (
      <WaiverEmail
        workItem={props.workItem}
        workItemDetails={props.workItemDetails}
      />
    );

  return (
    <div className={cn(styles.wrapper)}>
      <PageTitle>WAIVER WORK ITEM</PageTitle>
      <div className={cn(styles.title)}>
        {workItem.description}
        {workItem.airlineCode && (
          <AirlineSnoozer
            airlineCode={workItem.airlineCode}
            onSnooze={(airlineCode, hours) =>
              props.snoozeNotificationsForAirline(airlineCode, hours)
            }
          />
        )}
      </div>
      <div className={cn(styles.content)}>
        <WorkItemDataFields
          workItem={workItem}
          airlineLinks={props.workItemDetails.airlineInfoLinks}
          waivers={props.workItemWaivers}
        />
        <FormWithStatus status={props.updateStatus}>
          <WaiverWorkItemActionPicker
            workItem={props.workItem}
            workItemActions={props.workItemActions}
            executeAction={props.executeAction}
          />
          <CommentBox
            value={props.feedbackValue}
            onChange={props.feedbackOnChange}
            onSubmit={props.feedbackOnSubmit}
          />
        </FormWithStatus>
        {workItemContent}
      </div>
      <WaiverAssociationModal
        waiver={props.waiverToChangeAssociation}
        onSubmit={props.changeWaiverAssociation}
        onClose={props.endChangeWaiverAssociation}
      />
    </div>
  );
};

interface WorkItemDataFieldsProps{
  workItem: WaiverWorkItem;
  airlineLinks?: AirlineInfoLink[];
  waivers: Waiver[];
};

const WorkItemDataFields: React.FC<WorkItemDataFieldsProps> = ({
  workItem,
  airlineLinks,
  waivers,
}) => (
  <div className={cn(styles.workItemDataFields)}>
    <WorkItemDataField name="Notification ID">
      {workItem.changeEventId}
    </WorkItemDataField>
    <WorkItemDataField name="State">{workItem.state}</WorkItemDataField>
    <WorkItemDataField name="Resolution">
      {workItem.resolution}
    </WorkItemDataField>
    <WorkItemDataField name="Issue Date/Time">
      {workItem.createdAt.utc().format(getDateFormat(true))} UTC
    </WorkItemDataField>
    <WorkItemDataField name="Waivers Associated">
      {waivers.length &&
        waivers.map(waiver => (
          <WorkItemLink
            key={waiver.id}
            name={waiver.name}
            url={`/waivers/${waiver.id}/edit`}
          />
        ))}
    </WorkItemDataField>
    <WorkItemDataField name="Airline Info Links">
      {airlineLinks &&
        airlineLinks.map(link => (
          <WorkItemLink key={link.name} name={link.name} url={link.url} />
        ))}
    </WorkItemDataField>
  </div>
);

interface WorkItemDataFieldProps {
  name: string;
  children: React.ReactNode;
};

const WorkItemDataField: React.FC<WorkItemDataFieldProps> = ({ name, children }) => {
  if (!children) {
    return null;
  }
  return (
    <div className={cn('row')}>
      <div className={cn('col-sm-2', styles.workItemDataFieldLabel)}>
        {name}
      </div>
      <div className={cn('col-sm-10', styles.workItemDataFieldValue)}>
        {children}
      </div>
    </div>
  );
};

interface WorkItemLinkProps {
  name: string;
  url: string;
};

const WorkItemLink: React.FC<WorkItemLinkProps> = ({ name, url }) => (
  <div>
    <a
      className={cn(styles.workItemDataLink, styles.workItemLink)}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {name}
    </a>
  </div>
);

interface CommentBoxProps {
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: () => void;
  value: string;
};

const CommentBox: React.FC<CommentBoxProps> = (props) => {
  return (
    <>
      <TextArea
        value={props.value}
        onChange={props.onChange}
        name="feedback"
        placeholder="Feedback..."
      />
      <ButtonPrimary id="leaveFeedbackButton" onClick={props.onSubmit} disabled>
        Leave Feedback
      </ButtonPrimary>
    </>
  );
};

interface FormWithStatusProps {
  children: React.ReactNode;
  status: EventStatus;
};

const FormWithStatus: React.FC<FormWithStatusProps> = (props) => {
  const statusMessage = props.status.statusMessage
    ? props.status.statusMessage.message
    : null;
  const successClass = _.get(props.status.statusMessage, 'success')
    ? 'alert-success'
    : 'alert-danger';
  return (
    <form className={cn(styles.workItemInput)}>
      {statusMessage && !props.status.pending ? (
        <div className={cn(styles.updateMessage, styles.alert, successClass)}>
          {statusMessage}
        </div>
      ) : null}
      {props.children}
      {props.status.pending ? (
        <span className={cn(styles.spinnerWrap)}>
          <LoadingSpinner />
        </span>
      ) : null}
    </form>
  );
};

export default WaiverWorkItemComponent;
